import { createContext, useContext, useEffect, useState } from "react";
import { getSupabase, authedFetch } from "./supabase";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [supabase, setSupabase] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [verification, setVerification] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsub;
    (async () => {
      const sb = await getSupabase();
      setSupabase(sb);
      const { data: { session: s } } = await sb.auth.getSession();
      setSession(s);
      const sub = sb.auth.onAuthStateChange((_event, s2) => setSession(s2));
      unsub = sub.data.subscription;
      setLoading(false);
    })();
    return () => unsub?.unsubscribe?.();
  }, []);

  async function refreshProfile() {
    if (!session?.user) { setProfile(null); return null; }
    try {
      const sb = await getSupabase();
      const { data } = await sb.from("profiles").select("*").eq("id", session.user.id).maybeSingle();
      setProfile(data);
      return data;
    } catch (e) {
      console.warn("profile fetch failed", e);
      return null;
    }
  }

  useEffect(() => {
    if (!session?.user) {
      setProfile(null);
      setVerification(null);
      setVerificationLoading(false);
      return;
    }
    refreshProfile();
    refreshVerification();
  }, [session]);

  async function refreshVerification() {
    if (!session?.access_token) {
      setVerification(null);
      return null;
    }
    setVerificationLoading(true);
    try {
      const status = await authedFetch("/api/auth/verification/status");
      setVerification(status);
      return status;
    } catch (e) {
      console.warn("verification status fetch failed", e);
      setVerification({ verified: false, status: "pending", error: e.message });
      return null;
    } finally {
      setVerificationLoading(false);
    }
  }

  async function resendVerification() {
    const status = await authedFetch("/api/auth/verification/resend", { method: "POST" });
    setVerification(status);
    return status;
  }

  async function resendVerificationByEmail(email) {
    const res = await fetch("/api/auth/verification/resend-public", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.error || "Could not send confirmation email");
    return body;
  }

  async function signIn(email, password) {
    const sb = await getSupabase();
    const { error } = await sb.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }
  async function signUp(email, password, fullName) {
    const sb = await getSupabase();
    const { error } = await sb.auth.signUp({
      email, password,
      options: { data: { full_name: fullName } },
    });
    if (error) throw error;
    await resendVerificationByEmail(email);
  }
  async function signOut() {
    const sb = await getSupabase();
    await sb.auth.signOut();
  }

  return (
    <AuthCtx.Provider value={{
      supabase,
      session,
      profile,
      verification,
      verificationLoading,
      isVerified: Boolean(verification?.verified),
      loading,
      signIn,
      signUp,
      signOut,
      authedFetch,
      refreshProfile,
      refreshVerification,
      resendVerification,
      resendVerificationByEmail,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be inside <AuthProvider>");
  return v;
}
